/**
 * Payment Type Definitions
 * Represents payments, invoices, and transactions (Sprint 1: Mock UI with future-proof types)
 */

import type { CurrencyCode } from '../utils/formatting';

/**
 * Payment status
 */
export enum PaymentStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED',
  CANCELLED = 'CANCELLED',
}

/**
 * Payment method
 */
export enum PaymentMethod {
  CREDIT_CARD = 'CREDIT_CARD',
  DEBIT_CARD = 'DEBIT_CARD',
  BANK_TRANSFER = 'BANK_TRANSFER',
  ONLINE_BANKING = 'ONLINE_BANKING',
  E_WALLET = 'E_WALLET',
  CASH = 'CASH',
  CHEQUE = 'CHEQUE',
}

/**
 * Payment purpose/type
 */
export enum PaymentPurpose {
  APPLICATION_FEE = 'APPLICATION_FEE',
  CERTIFICATION_FEE = 'CERTIFICATION_FEE',
  RENEWAL_FEE = 'RENEWAL_FEE',
  CONSULTATION_FEE = 'CONSULTATION_FEE',
  AUDIT_FEE = 'AUDIT_FEE',
  TRAINING_FEE = 'TRAINING_FEE',
  PENALTY = 'PENALTY',
  OTHER = 'OTHER',
}

/**
 * Invoice status
 */
export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  ISSUED = 'ISSUED',
  SENT = 'SENT',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
}

/**
 * Payment entity (who is making/receiving payment)
 */
export interface PaymentEntity {
  id: string;
  name: string;
  type: 'ORGANIZATION' | 'USER';
  email: string;
  phone?: string;
  address?: string;
}

/**
 * Line item for invoices
 */
export interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number; // quantity * unitPrice
  taxRate?: number; // Percentage
  taxAmount?: number;
}

/**
 * Payment information
 */
export interface Payment {
  id: string;
  transactionId: string; // Unique transaction identifier
  amount: number;
  currency: CurrencyCode;
  status: PaymentStatus;

  // Parties
  payer: PaymentEntity;
  payee: PaymentEntity;

  // Purpose
  purpose: PaymentPurpose;
  description: string;
  referenceId?: string; // Related entity ID (application, training, etc.)

  // Payment details
  paymentMethod?: PaymentMethod;
  paymentDate?: Date;
  dueDate?: Date;

  // Gateway information (Sprint 1: mock only)
  gatewayProvider?: string;
  gatewayTransactionId?: string;
  gatewayResponse?: Record<string, any>;

  // Exchange rate (for multi-currency)
  exchangeRate?: number;
  baseCurrency?: CurrencyCode;
  baseAmount?: number;

  // Receipt
  receiptNumber?: string;
  receiptUrl?: string;
  receiptIssuedAt?: Date;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  processedBy?: string;
  processedAt?: Date;
}

/**
 * Invoice
 */
export interface Invoice {
  id: string;
  invoiceNumber: string; // e.g., "INV-2025-0001"
  status: InvoiceStatus;

  // Parties
  billFrom: PaymentEntity;
  billTo: PaymentEntity;

  // Dates
  issueDate: Date;
  dueDate: Date;
  paidDate?: Date;

  // Line items
  items: LineItem[];
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  currency: CurrencyCode;

  // Payment
  paymentId?: string; // Link to payment if paid
  paymentStatus: PaymentStatus;

  // Additional
  notes?: string;
  terms?: string;
  invoiceUrl?: string;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

/**
 * Payment audit trail entry (for compliance)
 */
export interface PaymentAuditEntry {
  timestamp: Date;
  action: string; // 'created', 'processed', 'completed', 'refunded', etc.
  actor: string; // User ID or system
  details: string;
  ipAddress?: string;
  metadata?: Record<string, any>;
}

/**
 * Payment record (immutable audit trail)
 */
export interface PaymentRecord {
  id: string;
  transactionId: string;
  payment: Payment;
  auditTrail: PaymentAuditEntry[];
  metadata: {
    paymentMethod?: PaymentMethod;
    gatewayResponse?: any;
    exchangeRate?: number;
    baseCurrency?: CurrencyCode;
  };
}

/**
 * Payment statistics
 */
export interface PaymentStatistics {
  totalRevenue: number;
  totalTransactions: number;
  pendingPayments: number;
  completedPayments: number;
  failedPayments: number;
  byPurpose: Record<PaymentPurpose, number>;
  byCurrency: Record<CurrencyCode, number>;
  averageTransactionAmount: number;
}

/**
 * Helper to check if payment is overdue
 */
export function isPaymentOverdue(payment: Payment): boolean {
  if (payment.status === PaymentStatus.COMPLETED || !payment.dueDate) {
    return false;
  }
  return new Date(payment.dueDate) < new Date();
}

/**
 * Helper to check if invoice is overdue
 */
export function isInvoiceOverdue(invoice: Invoice): boolean {
  if (invoice.status === InvoiceStatus.PAID || invoice.status === InvoiceStatus.CANCELLED) {
    return false;
  }
  return new Date(invoice.dueDate) < new Date();
}

/**
 * Helper to calculate invoice total
 */
export function calculateInvoiceTotal(items: LineItem[]): {
  subtotal: number;
  taxAmount: number;
  total: number;
} {
  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const taxAmount = items.reduce((sum, item) => sum + (item.taxAmount || 0), 0);
  const total = subtotal + taxAmount;

  return { subtotal, taxAmount, total };
}

/**
 * Helper to convert amount between currencies
 */
export function convertCurrency(
  amount: number,
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode,
  exchangeRate: number
): number {
  if (fromCurrency === toCurrency) return amount;
  return amount * exchangeRate;
}

/**
 * Helper to add audit entry
 */
export function createAuditEntry(
  action: string,
  actor: string,
  details: string,
  ipAddress?: string
): PaymentAuditEntry {
  return {
    timestamp: new Date(),
    action,
    actor,
    details,
    ipAddress,
  };
}
